import { Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalDocumentUploadDto } from '@asset-metrix/portal-gateway-client-js';
import {
  ErrorMessage,
  FileSelectionAction,
  FileSelectionState,
  FormatterOptions,
  Upload,
} from '@asset-metrix/portal-ui';
import { JsonError, JsonSchema } from '@sagold/react-json-editor';
import clsx from 'clsx';

import { WorkflowActionModalInputEditor } from './WorkflowActionModalInputEditor';

import styles from './workflowActionModal.module.scss';

type WorkflowActionModalInputProps = {
  closingSentence?: string;
  data: Record<string, unknown>;
  disabled: boolean;
  readOnly?: boolean;
  fileUpload?: ModalDocumentUploadDto & {
    dispatch: Dispatch<FileSelectionAction>;
    selections: FileSelectionState;
  };
  formattingOptions?: Record<string, FormatterOptions | undefined>;
  introducingSentence?: string;
  onChange: (data: Record<string, unknown>, errors: JsonError[]) => void;
  schema: JsonSchema;
};

export function WorkflowActionModalInput({
  closingSentence,
  data,
  disabled,
  readOnly = false,
  fileUpload,
  formattingOptions,
  introducingSentence,
  onChange,
  schema,
}: WorkflowActionModalInputProps) {
  const { t } = useTranslation();

  return (
    <div className={styles['input-section']}>
      {introducingSentence != null && (
        <div className={styles['input-section__introducing-sentence']}>
          {introducingSentence}
        </div>
      )}
      <div className={styles['input-section__editor']}>
        <WorkflowActionModalInputEditor
          data={data}
          onChange={onChange}
          schema={schema}
          formattingOptions={formattingOptions}
          disabled={disabled}
          readOnly={readOnly}
        />
      </div>
      {fileUpload != null && (
        <div
          className={clsx(styles['input-section__upload'], {
            [styles['input-section__upload--required']]: fileUpload.required,
          })}
        >
          <Upload
            accept={fileUpload.allowedMimeTypes}
            fileState={fileUpload.selections}
            dispatch={fileUpload.dispatch}
          />
          {fileUpload.required && fileUpload.selections.length === 0 && (
            <ErrorMessage className={styles['input-section__upload-error']}>
              {t('app.table.workflowActionModal.fileUploadRequired')}
            </ErrorMessage>
          )}
        </div>
      )}
      {closingSentence != null && (
        <div className={styles['input-section__closing-sentence']}>
          {closingSentence}
        </div>
      )}
    </div>
  );
}
